/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation } from 'react-query'

import {
  reprocessElasticsearchProject,
  reprocessObservabilityProject,
  reprocessSecurityProject,
  reprocessWorkplaceAIProject,
} from '@modules/project-admin-api/v1/callers'
import type { ProjectId } from '@modules/project-admin-api/v1/types'
import type { QueryFunctionReturnType } from '@modules/query/types'
import type { ProjectTypeWithWorkplaceAI } from '@modules/ui-types/projects'

/**
 * Returns the appropriate hook for the provided project type.
 */
export function getReprocessProjectMutationHook(
  projectType: ProjectTypeWithWorkplaceAI,
):
  | typeof useReprocessElasticsearchProjectMutation
  | typeof useReprocessObservabilityProjectMutation
  | typeof useReprocessSecurityProjectMutation
  | typeof useReprocessWorkplaceAIProjectMutation {
  switch (projectType) {
    case 'observability':
      return useReprocessObservabilityProjectMutation
    case 'security':
      return useReprocessSecurityProjectMutation
    case 'workplaceai':
      return useReprocessWorkplaceAIProjectMutation
    default:
      return useReprocessElasticsearchProjectMutation
  }
}

const useReprocessElasticsearchProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof reprocessElasticsearchProject>,
    unknown,
    { id: ProjectId }
  >({
    mutationFn: ({ id }) =>
      reprocessElasticsearchProject({
        pathParameters: { id },
      }),
  })

const useReprocessObservabilityProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof reprocessObservabilityProject>,
    unknown,
    { id: ProjectId }
  >({
    mutationFn: ({ id }) =>
      reprocessObservabilityProject({
        pathParameters: { id },
      }),
  })

const useReprocessSecurityProjectMutation = () =>
  useMutation<QueryFunctionReturnType<typeof reprocessSecurityProject>, unknown, { id: ProjectId }>(
    {
      mutationFn: ({ id }) =>
        reprocessSecurityProject({
          pathParameters: { id },
        }),
    },
  )

const useReprocessWorkplaceAIProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof reprocessWorkplaceAIProject>,
    unknown,
    { id: ProjectId }
  >({
    mutationFn: ({ id }) =>
      reprocessWorkplaceAIProject({
        pathParameters: { id },
      }),
  })
